import { Department, Person, Team } from "@office-map/api-schema";

import { getAuthenticatedUserInfo } from "@/features/auth/auth.utils";
import {
    commit,
    departments as departmensSource,
    people as peopleSource,
    teams as teamsSource,
} from "@/features/storage/data-provider";

type EmployeeWithMetadata = Person & {
    department?: string;
    team?: string;
};

class OrgRepository {
    departments = departmensSource;
    teams = teamsSource;
    people = peopleSource;

    private getTeamByEmployeeId = () =>
        Map.groupBy(
            this.teams.reduce(
                (acc, team) => {
                    return [
                        ...acc,
                        ...team.memberIds.map((memberId) => {
                            const member = this.people.find((person) => person.id === memberId)!;
                            return {
                                ...member,
                                teamId: team.id,
                                teamName: team.name,
                            };
                        }),
                    ];
                },
                [] as (Person & { teamId: string; teamName: string })[],
            ),
            (member) => member.id,
        );

    private getDepartmentByEmployeeId = () =>
        Map.groupBy(
            this.departments.reduce(
                (acc, department) => {
                    return [
                        ...acc,
                        ...(department.personIds ?? []).flatMap((memberId) => {
                            const member = this.people.find((person) => person.id === memberId)!;
                            return {
                                ...member,
                                departmentId: department.id,
                                departmentName: department.name,
                            };
                        }),
                        ...department.teamIds.flatMap((teamId) => {
                            const team = this.teams.find((team) => team.id === teamId)!;
                            return team.memberIds.map((memberId) => {
                                const member = this.people.find((person) => person.id === memberId)!;
                                return {
                                    ...member,
                                    departmentId: department.id,
                                    departmentName: department.name,
                                };
                            });
                        }),
                    ];
                },
                [] as (Person & { departmentId: string; departmentName: string })[],
            ),
            (member) => member.id,
        );

    findAllEmployees(): EmployeeWithMetadata[] {
        return this.people.map((person) => ({
            ...person,
            team: this.getTeamByEmployeeId().get(person.id)?.[0]?.teamName,
            department: this.getDepartmentByEmployeeId().get(person.id)?.[0]?.departmentName,
        }));
    }

    findAllDepartments() {
        return this.departments;
    }

    findAllTeams() {
        return this.teams;
    }

    findEmployeeById(employeeId: string): Omit<EmployeeWithMetadata, "seat"> | null {
        const person = this.people.find((p) => p.id === employeeId);
        if (!person) {
            return null;
        }

        return {
            ...person,
            team: this.getTeamByEmployeeId().get(person.id)?.[0]?.teamName,
            department: this.getDepartmentByEmployeeId().get(person.id)?.[0]?.departmentName,
        };
    }

    updateEmployee(employee: Person) {
        const personIdx = this.people.findIndex((p) => p.id === employee.id);
        if (personIdx === -1) {
            throw Error();
        }

        const { id, name, position, avatar } = employee;
        this.people[personIdx] = { id, name, position, avatar };

        commit();
    }

    addEmployee(employee: Omit<Person, "id">): Person {
        const employeeWithId = { id: self.crypto.randomUUID(), ...employee };
        this.people.push(employeeWithId);

        commit();

        return employeeWithId;
    }

    deleteEmployee(employee: Person) {
        const employeeTeamIdx = this.teams.findIndex((team) => team.memberIds.includes(employee.id));
        if (employeeTeamIdx !== -1) {
            this.teams[employeeTeamIdx].memberIds = this.teams[employeeTeamIdx].memberIds.filter(
                (memberId) => memberId != employee.id,
            );
        }
        const employeeDeptIdx = this.departments.findIndex((dept) => dept.personIds?.includes(employee.id));
        if (employeeDeptIdx !== -1) {
            this.departments[employeeDeptIdx].personIds = this.departments[employeeDeptIdx].personIds!.filter(
                (personId) => personId !== employee.id,
            );
        }
        this.people = this.people.filter((person) => person.id != employee.id);

        commit();
    }

    updateTeam(team: Team) {
        const teamIdx = this.teams.findIndex((t) => t.id === team.id);
        if (teamIdx === -1) {
            throw Error();
        }

        this.teams[teamIdx] = team;

        commit();
    }

    addTeam(team: Omit<Team, "id">): Team {
        const teamWithId = { id: self.crypto.randomUUID(), ...team };
        this.teams.push(teamWithId);

        commit();

        return teamWithId;
    }

    deleteTeam(team: Team) {
        this.departments.forEach((department) => {
            department.teamIds = department.teamIds.filter((teamId) => teamId !== team.id);
        });
        this.teams = this.teams.filter((t) => t.id !== team.id);

        commit();
    }

    updateDepartment(department: Department) {
        const departmentIdx = this.departments.findIndex((d) => d.id === department.id);
        if (departmentIdx === -1) {
            throw Error();
        }

        this.departments[departmentIdx] = department;

        commit();
    }

    addDepartment(department: Omit<Department, "id">): Department {
        const departmentWithId = { id: self.crypto.randomUUID(), ...department };
        this.departments.push(departmentWithId);

        commit();

        return departmentWithId;
    }

    deleteDepartment(department: Department) {
        this.departments = this.departments.filter((d) => d.id !== department.id);

        commit();
    }

    findEmployeeFromAuth(): Person | null {
        // TODO: Match by email once we begin to store emails.
        const { displayName } = getAuthenticatedUserInfo();

        const removeAccents = (value: string) =>
            value
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLocaleLowerCase();

        const displayNameNormalized = removeAccents(displayName);

        return this.people.find((p) => removeAccents(p.name) === displayNameNormalized) ?? null;
    }
}

export const orgRepository = new OrgRepository();
